// frontend/src/pages/dashboards/gridDrag.js
// Phase 4 step 14a-2: what a pointer does to the grid, as plain functions over
// a small state object, so every drag can be replayed in a test.
//
// A drag starts "pending" and becomes a real drag only after the pointer has
// moved a few pixels, so a click on a tile (to focus it, or on its own buttons)
// never moves anything. Every step is computed from the layout as it was when
// the drag began plus the total pointer movement, never from the previous
// step, so a fast or jittery pointer cannot drift. Escape cancels: the layout
// goes back exactly as it was.
import { GAP, ROW_HEIGHT, minSize, moveTile, resizeTile, spanPx, tileRect } from './gridLayout';

export const DRAG_THRESHOLD = 4;        // px of movement before a press becomes a drag

export const IDLE = Object.freeze({ mode: 'idle' });

/** A press on a tile. `action` is 'move' or 'resize'; `point` is {x, y} in client pixels. */
export function beginDrag(tiles, key, action, point) {
  const origin = tiles.find((t) => t.key === key);
  if (!origin || (action !== 'move' && action !== 'resize')) return IDLE;
  return { mode: 'pending', action, key, origin, base: tiles, preview: tiles, start: point, delta: { x: 0, y: 0 } };
}

/** The pointer moved. `colWidth` is the grid's column width in pixels at the time. */
export function dragTo(state, point, colWidth) {
  if (state.mode === 'idle') return state;
  const dx = point.x - state.start.x;
  const dy = point.y - state.start.y;
  if (state.mode === 'pending' && Math.hypot(dx, dy) < DRAG_THRESHOLD) return state;
  const cols = Math.round(dx / (colWidth + GAP));
  const rows = Math.round(dy / (ROW_HEIGHT + GAP));
  const o = state.origin;
  const preview = state.action === 'move'
    ? moveTile(state.base, state.key, o.x + cols, o.y + rows)
    : resizeTile(state.base, state.key, o.w + cols, o.h + rows);
  return { ...state, mode: 'dragging', delta: { x: dx, y: dy }, preview };
}

/** The pointer was released: the layout to keep (null when nothing changed) and the next state. */
export function endDrag(state) {
  const changed = state.mode === 'dragging' && state.preview !== state.base;
  return { tiles: changed ? state.preview : null, state: IDLE };
}

export const cancelDrag = () => IDLE;

/** The key of the tile being dragged, once it really is a drag. */
export const draggedKey = (state) => (state.mode === 'dragging' ? state.key : null);

/**
 * Where to draw the tile under the pointer while dragging, in pixels: it
 * follows the pointer freely (the snapped place is drawn underneath as the
 * placeholder), held inside the grid's width and below its top edge; while
 * resizing it never shrinks below its visual's minimum.
 */
export function ghostRect(state, colWidth, gridWidth) {
  if (state.mode !== 'dragging') return null;
  const r = tileRect(state.origin, colWidth);
  if (state.action === 'move') {
    return {
      ...r,
      left: Math.min(Math.max(0, r.left + state.delta.x), Math.max(0, gridWidth - r.width)),
      top: Math.max(0, r.top + state.delta.y),
    };
  }
  const min = minSize(state.origin);
  return {
    ...r,
    width: Math.min(Math.max(spanPx(min.w, colWidth), r.width + state.delta.x), Math.max(0, gridWidth - r.left)),
    height: Math.max(spanPx(min.h, ROW_HEIGHT), r.height + state.delta.y),
  };
}
